import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import useReveal from '../../hooks/useReveal';
import { BAR_ANIMATION_MS, REVEAL_DELAY_MS, REVEAL_DURATION_MS, toMs } from '../../constants/animation';

ChartJS.register(CategoryScale, LinearScale, PointElement, Tooltip, Legend);

interface AeoGeoSeoRendererProps {
  title: string;
  data: any;
  primaryColor?: string;
  secondaryColor?: string;
}

type Point = {
  name: string;
  shareOfVoice: number; // x
  sentiment: number; // y
  size?: number; // relative bubble size (e.g., volume or mentions)
  color?: string;
  sizeCategory?: 'X-Large' | 'Large' | 'Medium' | 'Small' | 'X-Small' | string;
};

function toPoints(raw: any): Point[] {
  // Accept shapes:
  // - { currentState: { points: [{ name, shareOfVoice, sentiment, size }] } }
  // - { points: [...] }
  // - Array of rows with keys similar to above
  if (!raw) return [];
  let pts: any[] = [];
  if (Array.isArray(raw)) pts = raw;
  if (Array.isArray(raw?.points)) pts = raw.points;
  if (Array.isArray(raw?.currentState?.points)) pts = raw.currentState.points;
  const norm = (v: any) => (typeof v === 'string' ? v.trim() : v);
  return (pts || [])
    .map((p: any) => ({
      name: String(p.name ?? p.label ?? p.brand ?? ''),
      shareOfVoice: Number(norm(p.shareOfVoice ?? p.sov ?? p.share ?? p.x ?? 0)),
      sentiment: Number(norm(p.sentiment ?? p.y ?? 0)),
      size: typeof p.size === 'number' ? p.size : Number(p.volume ?? p.count ?? 0),
      color: typeof p.color === 'string' ? p.color : undefined,
      sizeCategory: (p.sizeCategory || p.size_category || p.dotSize || p.dot_size || '').toString()
    }))
    .filter(p => p.name);
}

export default function AeoGeoSeoRenderer({ title, data, primaryColor, secondaryColor }: AeoGeoSeoRendererProps) {
  const points = toPoints(data);
  const [selected, setSelected] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    points.forEach(p => { init[p.name] = true; });
    return init;
  });
  const { ref: chartRef, isVisible: chartVisible } = useReveal<HTMLDivElement>({ threshold: 0.25, once: true });
  const { ref: opportunitiesRef, isVisible: opportunitiesVisible } = useReveal<HTMLDivElement>({ threshold: 0.15, once: true });
  const visiblePoints = useMemo(() => points.filter(p => selected[p.name] !== false), [points, selected]);
  const opportunitiesHtml: string | undefined = data?.opportunitiesText || data?.opportunities || data?.currentState?.opportunitiesText;
  const displayTitle = title === 'AEO/GEO/SEO' ? 'AI SEO (AEO/GEO/SEO)' : title;

  if (points.length === 0 && !opportunitiesHtml) return null;

  const maxSize = Math.max(1, ...points.map(p => Math.max(1, p.size || 1)));
  const minRadius = 8;
  const maxRadius = 42;
  const mapRadiusFromCategory = (cat?: string) => {
    const c = (cat || '').toLowerCase();
    if (c.includes('x-large') || c === 'xl') return 42;
    if (c.includes('large') && !c.includes('x-')) return 34;
    if (c.includes('medium') || c === 'm') return 26;
    if (c.includes('x-small') || c === 'xs') return 10;
    if (c.includes('small') || c === 's') return 18;
    return NaN;
  };
  const mapRadius = (value?: number, sizeCategory?: string) => {
    const cat = mapRadiusFromCategory(sizeCategory);
    if (!Number.isNaN(cat)) return cat;
    const v = typeof value === 'number' && value > 0 ? value : maxSize * 0.2;
    // area-proportional scaling (visual match to typical bubble charts)
    const scale = Math.sqrt(v / maxSize);
    return minRadius + scale * (maxRadius - minRadius);
  };

  const colors = useMemo(() => {
    const palette = [
      '#FF6B6B', '#4ECDC4', '#FFD166', '#8338EC', '#06D6A0',
      '#F72585', '#118AB2', '#EF476F', '#073B4C', '#F4A261', '#2A9D8F'
    ];
    const extras = [primaryColor, secondaryColor].filter((c): c is string => Boolean(c) && !palette.includes(c));
    return [...palette, ...extras];
  }, [primaryColor, secondaryColor]);

  const datasets = [{
    label: 'Share of Voice vs. Sentiment',
    showLine: false,
    data: visiblePoints.map(p => ({ x: p.shareOfVoice, y: p.sentiment, r: mapRadius(p.size, p.sizeCategory), _color: p.color, _name: p.name } as any)),
    pointBackgroundColor: (ctx: any) => {
      const c = ctx.raw?._color || colors[(ctx.dataIndex % colors.length)];
      return c + 'CC';
    },
    pointBorderColor: (ctx: any) => ctx.raw?._color || colors[(ctx.dataIndex % colors.length)],
    borderWidth: 2,
    pointRadius: (ctx: any) => (chartVisible ? ctx.raw?.r || 10 : 0),
    pointHoverRadius: (ctx: any) => (chartVisible ? (ctx.raw?.r || 10) + 2 : 0),
  }];

  const xValues = (visiblePoints.length ? visiblePoints : points).map(p => p.shareOfVoice);
  const yValues = (visiblePoints.length ? visiblePoints : points).map(p => p.sentiment);
  const pad = 5;
  const xMin = Math.min(-5, Math.floor((Math.min(...xValues) - pad) / 5) * 5);
  const xMax = Math.max(55, Math.ceil((Math.max(...xValues) + pad) / 5) * 5);
  const yMin = Math.min(-5, Math.floor((Math.min(...yValues) - pad) / 5) * 5); // ensure row below 0
  const yMax = Math.max(80, Math.ceil((Math.max(...yValues) + pad) / 5) * 5);

  const dataConfig = { datasets } as any;
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: chartVisible ? BAR_ANIMATION_MS : 0,
      easing: 'easeOutCubic',
      delay: chartVisible ? REVEAL_DELAY_MS : 0,
    },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.raw?._name || ''}: SOV ${ctx.parsed.x}%, Sentiment ${ctx.parsed.y}%` } },
    },
    scales: {
      x: {
        title: { display: true, text: 'Share of Voice (%)' },
        min: xMin,
        max: xMax,
        grid: { color: '#E5E7EB' },
      },
      y: {
        title: { display: true, text: 'Sentiment Score (%)' },
        min: yMin,
        max: yMax,
        grid: { color: '#E5E7EB' },
      },
    },
  } as const;

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900">{displayTitle}</h2>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Current State</h3>
            <div className="text-xs text-gray-500 mb-4">Share of Voice vs. Sentiment</div>
            {/* Clickable legend with brand toggles */}
            {points.length > 0 && (
              <div className="flex flex-wrap gap-4 mb-3 text-sm">
                {points.map((p, i) => {
                  const c = p.color || colors[i % colors.length];
                  const isOn = selected[p.name] !== false;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setSelected(prev => ({ ...prev, [p.name]: !isOn }))}
                      className={`flex items-center gap-2 px-2 py-1 rounded border ${isOn ? 'border-transparent' : 'border-gray-300'} ${isOn ? '' : 'opacity-50'}`}
                      title={isOn ? 'Click to hide' : 'Click to show'}
                    >
                      <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: c }} />
                      <span className="text-gray-700">{p.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div
              ref={chartRef}
              className="h-[420px]"
              style={{
                transition: `opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
                transitionDelay: chartVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
                opacity: chartVisible ? 1 : 0,
              }}
            >
              {visiblePoints.length > 0 ? <Scatter key={chartVisible ? 'chart-on' : 'chart-off'} data={dataConfig} options={options} /> : (
                <div className="h-full flex items-center justify-center text-gray-400">No data</div>
              )}
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Opportunities</h3>
            <div
              ref={opportunitiesRef}
              className=""
              style={{
                transition: `opacity ${toMs(REVEAL_DURATION_MS)} ease-out, transform ${toMs(REVEAL_DURATION_MS)} ease-out`,
                transitionDelay: opportunitiesVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
                opacity: opportunitiesVisible ? 1 : 0,
                transform: opportunitiesVisible ? 'translateY(0)' : 'translateY(16px)',
              }}
            >
              {opportunitiesHtml ? (
                <div
                  className="prose prose-sm max-w-none prose-ul:list-disc prose-li:marker:text-gray-600"
                  dangerouslySetInnerHTML={{ __html: String(opportunitiesHtml) }}
                />
              ) : (
                <div className="text-gray-500 text-sm">Add opportunities content in Admin → AEO/GEO/SEO.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
